import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Pagination,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { getRequest } from "../../utils/services";
import { useSnackbar } from "../../context/SnackbarContext";
import { FeedbackList } from "../../components/FeedbackList";

export default function Home() {
  const [userFeedbacks, setUserFeedbacks] = useState([]);
  const [receivedFeedbacks, setReceivedFeedbacks] = useState([]);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [page, setPage] = useState(1);
  const [currentFeedbackSection, setCurrentFeedbackSection] =
    useState("Meus Feedbacks");
  const cardsPerPage = 4;

  const { user, setUser, setIsUnauthorizedUser } = useContext(AuthContext);

  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchUserFeedbacks = async () => {
      setIsLoadingRequest(true);
      const response = await getRequest(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/feedback/consultarEnviados.php?user=${user.id_usuario}`
      );

      if (response.error) {
        if (response.statusCode === 401) {
          setIsUnauthorizedUser(true);
        }

        setIsLoadingRequest(false);
        return;
      }

      setIsLoadingRequest(false);
      setUserFeedbacks(response.dados);
    };

    if (user) {
      fetchUserFeedbacks();
    }
  }, []);

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("User");
    setUser(null);
    navigate("/");
  };

  const handleRedirectToFeedback = () => {
    navigate("/");
  };

  const handleCurrentSectionChange = async (section) => {
    setCurrentFeedbackSection(section);

    if (section === "Feedbacks Recebidos") {
      setIsLoadingRequest(true);

      const response = await getRequest(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/feedback/consultarRecebidos.php?user=${user.id_usuario}`
      );

      if (response.error) {
        showSnackbar(response.message, "error");

        if (response.statusCode === 401) {
          setIsUnauthorizedUser(true);
        }
        setIsLoadingRequest(false);
        return;
      }

      setIsLoadingRequest(false);
      setReceivedFeedbacks(response.dados);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        height: "90%",
        width: "90%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={5}
        sx={{
          height: "100%",
          width: "100%",
          p: 4,
          borderRadius: 2,
          overflowX: "auto",
          maxHeight: "95%",
          display: "flex",
        }}
      >
        <Box
          sx={{
            width: "280px",
            borderRight: "1px solid #e0e0e0",
            pr: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#014F6D",
                fontFamily: "JetBrains Mono, sans-serif",
              }}
            >
              Olá, {user?.nome}!
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#666", fontFamily: "Montserrat, sans-serif" }}
            >
              Bem-vindo ao painel de feedbacks
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              height: "100%",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Button
                onClick={() => handleCurrentSectionChange("Meus Feedbacks")}
                variant={
                  currentFeedbackSection === "Meus Feedbacks"
                    ? "contained"
                    : "outlined"
                }
                sx={{
                  mb: 2,
                  justifyContent: "flex-start",
                  fontFamily: "JetBrains Mono, sans-serif",
                }}
              >
                Feedbacks Enviados
              </Button>
              {user.setores.length > 0 && (
                <Button
                  onClick={() =>
                    handleCurrentSectionChange("Feedbacks Recebidos")
                  }
                  variant={
                    currentFeedbackSection === "Feedbacks Recebidos"
                      ? "contained"
                      : "outlined"
                  }
                  sx={{
                    mb: 2,
                    fontFamily: "JetBrains Mono, sans-serif",
                    justifyContent: "flex-start",
                  }}
                >
                  Feedbacks Recebidos
                </Button>
              )}
            </Box>
            <Box>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  fontFamily: "JetBrains Mono, sans-serif",
                  justifyContent: "flex-start",
                }}
              >
                Sair
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            pl: 4,
            display: "flex",
            flexDirection: "column",
            width: "50%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontFamily: "JetBrains Mono, sans-serif",
                color: "#014F6D",
              }}
            >
              Feedbacks Recentes
            </Typography>
            {currentFeedbackSection === "Meus Feedbacks" && (
              <Button
                onClick={handleRedirectToFeedback}
                variant="contained"
                sx={{
                  fontFamily: "JetBrains Mono, sans-serif",
                }}
              >
                Novo Feedback
              </Button>
            )}
          </Box>

          <FeedbackList
            userFeedbacks={
              currentFeedbackSection === "Feedbacks Recebidos"
                ? receivedFeedbacks
                : userFeedbacks
            }
            page={page}
            isLoading={isLoadingRequest}
            cardsPerPage={cardsPerPage}
            currentSection={currentFeedbackSection}
          />

          {(currentFeedbackSection === "Feedbacks Recebidos"
            ? receivedFeedbacks.length
            : userFeedbacks.length) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(
                  (currentFeedbackSection === "Feedbacks Recebidos"
                    ? receivedFeedbacks.length
                    : userFeedbacks.length) / cardsPerPage
                )}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
